import * as React from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import { DriverModel } from '@/app/models'
import { ImiExportModel } from '@/app/models/export/imiExportModel'
import { validateImiModel } from '@/app/models/export/imiExportModelValidator'
import { getDifferenceBetweenDatesInDays } from '@/app/common/dateHelper'
import { OperationType } from '@/app/constants/operationType'
import { TransportType } from '@/app/constants/transportType'
import { TaskType } from '@/app/constants/taskType'
import { getOperationTypeName } from '@/app/common/converters/export/operationTypeToTextConverter'
import { getTransportTypeName } from '@/app/common/converters/export/transportTypeToTextConverter'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useTask } from '@/modules/shared/context/TaskContext'

import { getApiErrorMessage } from '@/modules/shared/utils/apiError'
import { toast } from '@/lib/toasts/api'
import { DateField } from '@/ui/form/controls/DateField'
import { Button } from '@/ui'
import { FieldsetMultiSelect } from '@/modules/settings/components/FieldsetMultiSelect'
import { FaIcon, faArrowLeft, faStar } from '@/lib/fa'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import type { Driver } from '@/lib/api/company/drivers/types'
import ImiDriverValidationTable from '../components/imiValidationTable/ImiDriverValidationTable'
import { useImiSidePanel } from '../context/ImiSidePanelContext'

import { useLazyFullDrivers } from '../hooks/useLazyFullDrivers'
import { useQueryVehicles } from '../services/useQueryVehicles'
import { useQueryExportCountries } from '../services/useQueryExportCountries'
import { useCreateImiDeclaration } from '../services/useImiDeclarationsService'
import { useImiFavorites } from '../services/useImiFavoritesService'
import { TaskStatus } from '@/modules/shared/components'
import {
  useImiExportErrors,
  useImiExportErrorsStore,
} from '../stores/imiExportErrorsStore'

type MatchParams = {
  companyId: string
}

const OPERATION_TYPES: Array<OperationType> = [
  OperationType.Cabotage,
  OperationType.InternationalCarriage,
]

const TRANSPORT_TYPES: Array<TransportType> = [
  TransportType.CarriageOfGoods,
  TransportType.CarriageOfPeople,
]

const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'ES',
  'CY',
  'GR',
  'NL',
  'IE',
  'LT',
  'LV',
  'LU',
  'DE',
  'PT',
  'RO',
  'SK',
  'SE',
  'IT',
  'HU',
  'GB',
  'SI',
  'MT',
  'NO',
  'LI',
  'IS',
]

const DRIVER_VALIDATION_FIELD_KEYS: Record<string, string> = {
  address: 'exportModule.driver-field-address',
  postalCode: 'exportModule.driver-field-postalCode',
  city: 'exportModule.driver-field-city',
  country: 'exportModule.driver-field-country',
  birthday: 'exportModule.driver-field-birthday',
  drivingLicenceNumber: 'exportModule.driver-field-drivingLicenceNumber',
  surname: 'exportModule.driver-field-surname',
  name: 'exportModule.driver-field-name',
  workStartTime: 'exportModule.driver-field-workStartTime',
  driverIdCardNumber: 'exportModule.driver-field-driverIdCardNumber',
  driverIdCardNumberFormat: 'imiExport.validation.driverIdCardNumberFormat',
  driverIdCardCountry: 'exportModule.driver-field-driverIdCardCountry',
  missingActiveContract: 'exportModule.driver-field-missingActiveContract',
}

const getDefaultDateRange = () => {
  const now = moment()
  return {
    dateFrom: now.format('YYYY-MM-DD'),
    dateTo: now.clone().add(6, 'months').add(-1, 'days').format('YYYY-MM-DD'),
  }
}

const getDriverLabel = (d: Driver) => {
  const raw = d.fullName?.trim()
  if (raw) return raw
  return `${d.surname ?? ''} ${d.name ?? ''}`.trim()
}

const AddImiDeclarationPage: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const { companyId } = match.params
  const exportErrors = useImiExportErrors(companyId)
  const { t, activeLanguage } = useLocalize()
  const navigate = useNavigate()
  const { fetchDriver, basicDrivers } = useDrivers()
  const { addTask } = useTask()
  const createMutation = useCreateImiDeclaration(companyId)
  const { favorites, toggleFavorite } = useImiFavorites(companyId)

  const {
    selectedDriverIds: driverIds,
    setSelectedDriverIds: setDriverIds,
    bumpDriverTableKey,
  } = useImiSidePanel()

  const [dateFrom, setDateFrom] = React.useState(getDefaultDateRange().dateFrom)
  const [dateTo, setDateTo] = React.useState(getDefaultDateRange().dateTo)
  const [countryCodes, setCountryCodes] = React.useState<string[]>([])
  const [operationTypes, setOperationTypes] = React.useState<OperationType[]>(
    [],
  )
  const [transportTypes, setTransportTypes] = React.useState<TransportType[]>(
    [],
  )
  const [vehicleNumbers, setVehicleNumbers] = React.useState<string[]>([])
  const [submitted, setSubmitted] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  // Reset this page's own form fields on company change. The driver selection
  // and the side list itself are reset by ReportsLayout.
  const prevCompanyRef = React.useRef(companyId)
  React.useEffect(() => {
    if (prevCompanyRef.current === companyId) return
    prevCompanyRef.current = companyId
    const defaults = getDefaultDateRange()
    setDateFrom(defaults.dateFrom)
    setDateTo(defaults.dateTo)
    setCountryCodes([])
    setOperationTypes([])
    setTransportTypes([])
    setVehicleNumbers([])
    setSubmitted(false)
  }, [companyId])

  const activeDrivers = React.useMemo(
    () => basicDrivers.filter((d) => d.isActive !== false),
    [basicDrivers],
  )

  const { data: vehicles = [], isLoading: vehiclesLoading } = useQueryVehicles({
    companyId,
  })
  const { data: countries = [], isLoading: countriesLoading } =
    useQueryExportCountries({ companyId })

  const { fullDrivers, loadingDriverIds } = useLazyFullDrivers(
    driverIds,
    fetchDriver,
  )

  const filteredCountries = React.useMemo(
    () =>
      countries.filter(
        (country) => country.code && EU_COUNTRIES.includes(country.code),
      ),
    [countries],
  )

  const countryOptions = React.useMemo(
    () => filteredCountries.map((c) => ({ value: c.code, label: c.name })),
    [filteredCountries],
  )

  const operationOptions = React.useMemo(
    () =>
      OPERATION_TYPES.map((ot) => ({
        value: ot.toString(),
        label: getOperationTypeName(ot, t),
      })),
    [t],
  )

  const transportOptions = React.useMemo(
    () =>
      TRANSPORT_TYPES.map((tt) => ({
        value: tt.toString(),
        label: getTransportTypeName(tt, t),
      })),
    [t],
  )

  const vehicleOptions = React.useMemo(
    () =>
      vehicles

        .filter((v) => v.isActive)
        .map((v) => ({
          value: v.registrationNumber,
          label: v.registrationNumber,
        })),
    [vehicles],
  )

  const selectedDriversFromList = basicDrivers.filter((driver) =>
    driverIds.includes(driver.id.toString()),
  )

  const driversValidationInfo = selectedDriversFromList.map(
    (driverListInfo) => {
      const id = driverListInfo.id.toString()
      const full = fullDrivers[id]
      const isLoading = loadingDriverIds.has(id)

      const driverForValidation = full || driverListInfo
      const validationErrorsRaw = !isLoading
        ? validateImiModel(driverForValidation as DriverModel)
        : []
      const validationErrors = validationErrorsRaw.filter(
        (errorKey: string) => errorKey !== 'missingActiveContract',
      )
      const missingFields = validationErrors.map((errorKey: string) =>
        t(DRIVER_VALIDATION_FIELD_KEYS[errorKey] || errorKey),
      )

      return {
        id,
        name: getDriverLabel(driverListInfo),
        isLoading,
        hasError: validationErrors.length > 0,
        missingFields,
        apiError: exportErrors[id],
        driverForSubmit: {
          ...(full || driverListInfo),
          id: driverListInfo.id,
          driverId: (driverListInfo as any).driverId || full?.driverId,
        } as DriverModel,
      }
    },
  )

  const selectedCountries = filteredCountries.filter(
    (country) => country.code && countryCodes.includes(country.code),
  )
  const dateFromMoment = moment(dateFrom, 'YYYY-MM-DD', true)
  const dateToMoment = moment(dateTo, 'YYYY-MM-DD', true)
  const durationDays =
    dateFromMoment.isValid() && dateToMoment.isValid()
      ? getDifferenceBetweenDatesInDays(dateFromMoment, dateToMoment)
      : '-'

  const hasDriverError = driverIds.length === 0
  const hasDriverDataLoadingError = driversValidationInfo.some(
    (d) => d.isLoading,
  )
  const hasDriverDataError = driversValidationInfo.some((d) => d.hasError)
  const hasDateError =
    !dateFromMoment.isValid() ||
    !dateToMoment.isValid() ||
    durationDays === '-' ||
    durationDays > 183
  const hasCountryError = selectedCountries.length === 0
  const hasOperationTypeError = operationTypes.length === 0
  const hasTransportTypeError = transportTypes.length === 0
  const hasVehicleError = vehicleNumbers.length === 0

  const formattedDateRange = React.useMemo(() => {
    const from = dateFromMoment.isValid()
      ? dateFromMoment.format('DD-MM-YYYY')
      : dateFrom
    const to = dateToMoment.isValid()
      ? dateToMoment.format('DD-MM-YYYY')
      : dateTo
    return `${from} – ${to}`
  }, [dateFromMoment, dateToMoment, dateFrom, dateTo])

  const countriesText = React.useMemo(
    () => selectedCountries.map((c) => c.name).join(', '),
    [selectedCountries],
  )

  const operationTypesText = React.useMemo(
    () => operationTypes.map((ot) => getOperationTypeName(ot, t)).join(', '),
    [operationTypes, t],
  )

  const transportTypesText = React.useMemo(
    () => transportTypes.map((tt) => getTransportTypeName(tt, t)).join(', '),
    [transportTypes, t],
  )

  const vehiclesText = React.useMemo(
    () => vehicleNumbers.join(', '),
    [vehicleNumbers],
  )

  const isFormValid =
    !hasDriverError &&
    !hasDriverDataLoadingError &&
    !hasDriverDataError &&
    !hasDateError &&
    !hasCountryError &&
    !hasOperationTypeError &&
    !hasTransportTypeError &&
    !hasVehicleError

  const goBack = () => navigate('../imi-declarations')

  const hasAnyFavorites =
    favorites.countries.length > 0 ||
    favorites.vehicles.length > 0 ||
    favorites.operationTypes.length > 0 ||
    favorites.transportTypes.length > 0 ||
    favorites.drivers.length > 0

  const applyFavorites = () => {
    const availableCountryCodes = new Set(countryOptions.map((o) => o.value))
    const availableVehicleNumbers = new Set(vehicleOptions.map((o) => o.value))
    const availableDriverIds = new Set(
      activeDrivers.map((d) => d.id.toString()),
    )

    if (countryCodes.length === 0) {
      setCountryCodes(
        favorites.countries.filter((c) => availableCountryCodes.has(c)),
      )
    }
    if (vehicleNumbers.length === 0) {
      setVehicleNumbers(
        favorites.vehicles.filter((v) => availableVehicleNumbers.has(v)),
      )
    }
    if (operationTypes.length === 0) {
      setOperationTypes(
        favorites.operationTypes
          .map((v) => Number(v))
          .filter((v) =>
            OPERATION_TYPES.includes(v as OperationType),
          ) as OperationType[],
      )
    }
    if (transportTypes.length === 0) {
      setTransportTypes(
        favorites.transportTypes
          .map((v) => Number(v))
          .filter((v) =>
            TRANSPORT_TYPES.includes(v as TransportType),
          ) as TransportType[],
      )
    }
    if (driverIds.length === 0 && favorites.drivers.length > 0) {
      const newDriverIds = favorites.drivers.filter((id) =>
        availableDriverIds.has(id),
      )
      setDriverIds(newDriverIds)
      bumpDriverTableKey()
    }
  }

  const handleSubmit = async () => {
    setSubmitted(true)
    if (driverIds.length === 0 || !isFormValid) return

    const models: ImiExportModel[] = driversValidationInfo.map((d) => ({
      driver: d.driverForSubmit,
      dateFrom: dateFromMoment,
      dateTo: dateToMoment,
      country: selectedCountries,
      isValid: true,
      vehicles: vehicleNumbers,
      transportType: transportTypes,
      operationType: operationTypes,
      validation: null,
    }))

    const serializedModels = models.map((model) => ({
      ...model,
      dateFrom: model.dateFrom?.format?.('YYYY-MM-DD') ?? model.dateFrom,
      dateTo: model.dateTo?.format?.('YYYY-MM-DD') ?? model.dateTo,
    }))

    setIsSubmitting(true)

    try {
      const taskId = uuidv4()
      await createMutation.mutateAsync({
        importId: 0,
        options: {
          models: serializedModels,
          language: activeLanguage?.code || 'pl',
        },
        taskId,
      })
      useImiExportErrorsStore.getState().clearErrors(companyId, driverIds)
      addTask(taskId, TaskType.ImiExport, null)

      toast.success({
        text:
          t('exportModule.add-declaration-success') +
          (serializedModels.length > 1 ? ` (${serializedModels.length})` : ''),
      })
      goBack()
    } catch (err: any) {
      toast.error({
        text: t('exportModule.add-declaration-error'),
        content: getApiErrorMessage(err) || err?.message || 'Błąd',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="tw-relative tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col">
      <div className="tw-absolute tw-right-0 tw-top-0 tw-z-[101]">
        <TaskStatus />
      </div>
      <div className="tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-p-4">
          <div className="tw-mb-4 tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-3">
            <div className="tw-flex tw-items-center tw-gap-4">
              <FaIcon
                onClick={goBack}
                icon={faArrowLeft}
                className="tw-h-5 tw-w-5 tw-cursor-pointer hover:tw-opacity-70"
              />
              <h2 className="tw-text-lg tw-font-semibold tw-text-content-base">
                {t('exportModule.add-declaration-title')}
              </h2>
            </div>

            <div
              onClick={applyFavorites}
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 hover:tw-underline hover:tw-opacity-80"
            >
              <p className="tw-font-medium tw-text-primary">
                {t('exportModule.apply-favorites') || 'Zastosuj ulubione'}
              </p>
              <FaIcon className="tw-text-yellow-400" icon={faStar} />
            </div>
          </div>

          {submitted && hasDriverError && (
            <div className="tw-mb-3 tw-text-sm tw-font-medium tw-text-red-600">
              {t('exportModule.validation-driver-required')}
            </div>
          )}

          <div className="tw-grid tw-shrink-0 tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
            <label className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium">
                {t('exportModule.form-date-from')}
              </span>
              <DateField
                value={dateFrom}
                setValue={(value) => {
                  const val = value || ''
                  setDateFrom(val)
                  const m = moment(val, 'YYYY-MM-DD', true)
                  if (m.isValid()) {
                    setDateTo(
                      m
                        .clone()
                        .add(6, 'months')
                        .add(-1, 'days')
                        .format('YYYY-MM-DD'),
                    )
                  }
                }}
                error={
                  submitted && hasDateError
                    ? t('exportModule.validation-date-range')
                    : null
                }
                disabled={isSubmitting}
              />
            </label>
            <label className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium">
                {t('exportModule.form-date-to')}
              </span>
              <DateField
                value={dateTo}
                setValue={(value) => setDateTo(value || '')}
                error={
                  submitted && hasDateError
                    ? t('exportModule.validation-date-range')
                    : null
                }
                disabled={isSubmitting}
              />
            </label>

            <FieldsetMultiSelect
              label={t('exportModule.form-country')}
              placeholder={t('exportModule.form-country')}
              name="countries"
              className="tw-z-[60] [&:has(ul)]:tw-z-[80]"
              value={countryCodes}
              options={countryOptions}
              disabled={isSubmitting || countriesLoading}
              listMaxHeight={400}
              showListScrollbar
              error={
                submitted && hasCountryError
                  ? t('exportModule.validation-country-required')
                  : undefined
              }
              handleChange={(values) => setCountryCodes(values)}
              favoriteValues={favorites.countries}
              onToggleFavorite={(value) => toggleFavorite('countries', value)}
              showSelectAll
            />

            <FieldsetMultiSelect
              label={t('exportModule.form-vehicles')}
              placeholder={t('exportModule.form-vehicles')}
              name="vehicles"
              className="tw-z-[60] [&:has(ul)]:tw-z-[80]"
              value={vehicleNumbers}
              options={vehicleOptions}
              disabled={isSubmitting || vehiclesLoading}
              listMaxHeight={400}
              showListScrollbar
              error={
                submitted && hasVehicleError
                  ? t('exportModule.validation-vehicles-required')
                  : undefined
              }
              handleChange={(values) => setVehicleNumbers(values)}
              favoriteValues={favorites.vehicles}
              onToggleFavorite={(value) => toggleFavorite('vehicles', value)}
              showSelectAll
            />

            <FieldsetMultiSelect
              label={t('exportModule.form-operation-type')}
              placeholder={t('exportModule.form-operation-type')}
              name="operationTypes"
              className="tw-z-[60] [&:has(ul)]:tw-z-[80]"
              value={operationTypes.map(String)}
              options={operationOptions}
              disabled={isSubmitting}
              error={
                submitted && hasOperationTypeError
                  ? t('exportModule.validation-operation-type-required')
                  : undefined
              }
              handleChange={(values) =>
                setOperationTypes(values.map((v) => Number(v) as OperationType))
              }
              favoriteValues={favorites.operationTypes}
              onToggleFavorite={(value) =>
                toggleFavorite('operationTypes', value)
              }
            />

            <FieldsetMultiSelect
              label={t('exportModule.form-transport-type')}
              placeholder={t('exportModule.form-transport-type')}
              name="transportTypes"
              className="tw-z-[60] [&:has(ul)]:tw-z-[80]"
              value={transportTypes.map(String)}
              options={transportOptions}
              disabled={isSubmitting}
              error={
                submitted && hasTransportTypeError
                  ? t('exportModule.validation-transport-type-required')
                  : undefined
              }
              handleChange={(values) =>
                setTransportTypes(values.map((v) => Number(v) as TransportType))
              }
              favoriteValues={favorites.transportTypes}
              onToggleFavorite={(value) =>
                toggleFavorite('transportTypes', value)
              }
            />
          </div>

          {/* Selected drivers validation summary */}
          {driverIds.length > 0 && (
            <div className="tw-mt-4 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100">
              {submitted && hasDriverDataLoadingError && (
                <div className="tw-shrink-0 tw-p-3 tw-text-xs tw-font-medium tw-text-red-600">
                  {t('exportModule.validation-driver-loading')}
                </div>
              )}
              <ImiDriverValidationTable
                drivers={driversValidationInfo}
                submitted={submitted}
                dateRange={formattedDateRange}
                countriesText={countriesText}
                operationTypesText={operationTypesText}
                transportTypesText={transportTypesText}
                vehiclesText={vehiclesText}
              />
            </div>
          )}

          <div className="tw-mt-auto tw-flex tw-shrink-0 tw-justify-end tw-gap-2 tw-border-t tw-border-base-300 tw-pt-4">
            <Button
              onClick={goBack}
              text={t('common.cancel')}
              size="sm"
              variant="tertiary"
              disabled={isSubmitting}
            />
            <Button
              onClick={handleSubmit}
              text={
                isSubmitting
                  ? t('exportModule.submitting-in-progress')
                  : t('exportModule.add-declaration-submit')
              }
              size="sm"
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting || countriesLoading || vehiclesLoading}
            />
          </div>
        </div>
    </div>
  )
}

export { AddImiDeclarationPage }
export default AddImiDeclarationPage
